/**
 * Navigation utilities for handling links and clicks
 */

/**
 * Creates a click handler that supports middle-click and Ctrl/Cmd+click to open in new tab
 *
 * @param {string} url - The URL to navigate to
 * @param {Function} onNavigate - Callback function for normal navigation (left click)
 * @returns {Function} Event handler function
 *
 * @example
 * const handleClick = createNavigationHandler('/operations/tm-log/report/123', () => {
 *   navigate('/operations/tm-log/report/123');
 * });
 *
 * <div onClick={handleClick} onMouseDown={handleClick} onAuxClick={handleClick}>
 *   Click me
 * </div>
 */
export const createNavigationHandler = (url, onNavigate) => {
  return (e) => {
    // Middle click (button 1) or Ctrl/Cmd+click - open in new tab
    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      e.preventDefault();
      window.open(url, '_blank');
    }
    // Left click (button 0) - normal navigation
    else if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
      if (onNavigate) {
        onNavigate();
      }
    }
  };
};

/**
 * Creates props object for an element that should support middle-click navigation
 *
 * @param {string} url - The URL to navigate to
 * @param {Function} onNavigate - Callback function for normal navigation
 * @returns {Object} Props object with onClick, onMouseDown, and onAuxClick handlers
 *
 * @example
 * const navProps = createNavigationProps('/operations/tm-log/report/123', () => {
 *   navigate('/operations/tm-log/report/123');
 * });
 *
 * <div {...navProps}>Click me</div>
 */
export const createNavigationProps = (url, onNavigate) => {
  const handler = createNavigationHandler(url, onNavigate);
  return {
    onClick: handler,
    onMouseDown: handler,
    onAuxClick: handler,
  };
};

/**
 * Returns the Django admin panel URL for a backend base URL.
 *
 * @param {string|null} baseURL - Backend base URL.
 * @returns {string} Admin panel URL.
 */
export const getAdminPanelUrl = (baseURL) => {
  return `${baseURL || "/"}admin/`;
};

/**
 * Opens the Django admin panel in a new browser tab.
 *
 * @param {string|null} baseURL - Backend base URL.
 * @returns {Window|null} Opened browser window, or null if blocked.
 */
export const openAdminPanel = (baseURL) => {
  return window.open(getAdminPanelUrl(baseURL), "_blank", "noopener,noreferrer");
};
